import "../../kendo.button.js"; // Still required until kendo.ui is modularized
import "../../kendo.icons.js";
import { htmlService } from "../../core/services/html.service";
import { STYLES, REFERENCES, ICONS, FILES_LAYOUT_MODE } from "../constants";
import type { IChatMessages, FilesLayoutMode } from "../models";
import type { IFile } from "../../upload";
import { renderFile } from "./common.template";

declare const kendo: any;

/**
 * Renders a list of file attachments with the specified layout mode.
 * @param files - Array of files to render
 * @param downloadAll - Whether to show a "download all" button
 * @param messages - Localization messages
 * @param closeButton - Whether to show close buttons on files
 * @param layoutMode - Layout mode: "horizontal" | "vertical" | "wrap"
 */
export const renderFiles = (
    files: IFile[] | undefined,
    downloadAll: boolean,
    messages: IChatMessages,
    closeButton?: boolean,
    layoutMode: FilesLayoutMode = FILES_LAYOUT_MODE.VERTICAL
): string => {
    if (!files?.length) {
        return "";
    }

    let fileItems = "";
    files.forEach(file => {
        fileItems += renderFile(file, closeButton ?? false, true);
    });

    let layoutClass = "";
    if (layoutMode === FILES_LAYOUT_MODE.VERTICAL) {
        layoutClass = ` ${STYLES.filesVertical}`;
    } else if (layoutMode === FILES_LAYOUT_MODE.WRAP) {
        layoutClass = ` ${STYLES.filesWrap}`;
    }

    let html = `<ul class="${STYLES.fileWrapper}${layoutClass}" ${REFERENCES.fileWrapper}>`;

    if (layoutMode === FILES_LAYOUT_MODE.HORIZONTAL) {
        html += `<div class="${STYLES.filesScroll}">${fileItems}</div>`;
    } else {
        html += fileItems;
    }

    html += "</ul>";

    if (downloadAll && files.length > 1 && messages?.downloadAll) {
        html += `<div class="${STYLES.downloadButtonWrapper}">
            ${new kendo.ui.Button(`<button class="${STYLES.downloadButton}">${messages.downloadAll}</button>`, {
                icon: ICONS.download,
                fillMode: "flat",
                size: "small"
            }).wrapper[0].outerHTML}
        </div>`;
    }

    return html;
};

/**
 * Renders a message reference (for reply or pinned messages).
 */
export const renderMessageReference = (
    context: {
        text?: string;
        files?: IFile[];
        isOwnMessage?: boolean;
        isPinMessage?: boolean;
        isDeleted?: boolean;
        renderCloseButton?: boolean;
        renderFileMenuButton?: boolean;
        messages?: IChatMessages;
    }
): string => {
    const {
        text,
        files,
        isOwnMessage,
        isPinMessage,
        isDeleted,
        renderCloseButton,
        renderFileMenuButton,
        messages
    } = context;

    const messageReferenceSenderStyle = isOwnMessage
        ? STYLES.messageReferenceSender
        : STYLES.messageReferenceReceiver;
    const pinMessageReferenceStyle = isPinMessage ? STYLES.messagePinned : "";
    const closeButtonReference = isPinMessage
        ? REFERENCES.pinnedMessageCloseButton
        : REFERENCES.replyMessageCloseButton;
    const wrapperReference = isPinMessage
        ? REFERENCES.messageReferencePinWrapper
        : REFERENCES.messageReferenceReplyWrapper;

    let content = htmlService.convertTextUrlToLink(text || "");

    if (!content) {
        content = files?.length ? renderFile(files[0], false, renderFileMenuButton ?? false) : "";
    }

    if (isDeleted && messages) {
        content = isOwnMessage
            ? htmlService.encode(messages.selfMessageDeleted)
            : htmlService.encode(messages.otherMessageDeleted);
    }

    return `<div class="${STYLES.messageReference} ${messageReferenceSenderStyle} ${pinMessageReferenceStyle}" ${wrapperReference}>
        ${isPinMessage ? kendo.ui.icon({ icon: ICONS.pin, size: "xlarge" }) : ""}
        <div class="${STYLES.messageReferenceContent}">${content}</div>
        <span class="${STYLES.spacer}"></span>
        ${renderCloseButton ? new kendo.ui.Button(`<button ${closeButtonReference}>`, {
            icon: ICONS.x,
            fillMode: "flat"
        }).wrapper[0].outerHTML : ""}
    </div>`;
};
